import { EventRuleAction, EventRuleIdentifier, EventType, Pagination, Task } from '@ekkogmbh/apisdk';
import { Typography } from '@mui/material';
import { green, red, yellow } from '@mui/material/colors';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { DataTable } from 'src/Common/Components/DataTable';
import { JsonPathParameterDescription } from 'src/Common/Components/DataTable/Descriptions';
import RawJsonField from 'src/Common/Components/DataTable/RawJsonField';
import ScrollableStack from 'src/Common/Components/ScrollableStack';
import { CancelableFetchPromises, cancelFetchPromises } from 'src/Common/Helper/PromiseHelper';
import useEkko from 'src/Common/Providers/EkkoProvider';

const ROW_MIN_HEIGHT = 64;

// --- headline helpers ---
const makeParamHeadline = ({ instruction: { parameters, method } }: Task): Record<string, string> | undefined => {
  switch (method) {
    case 'eventRuleExecution': {
      try {
        const action = parameters.action as EventRuleAction;
        const { event, rule } = parameters.message as {
          event: { data: { type: EventType }; coordinate: string };
          rule: EventRuleIdentifier;
        };

        return {
          action: (action as EventRuleAction).type,
          event: `${event.data.type} [${event.coordinate}]`,
          rule: `${rule.name} [${rule.coordinate}]`,
        };
      } catch (e) {
        console.log(JSON.stringify(e));
        return undefined;
      }
    }
    default: {
      const { linkId, deviceId, labelId } = parameters;
      if (typeof deviceId === 'string') {
        return { deviceId };
      }

      if (typeof linkId === 'number') {
        return { linkId: linkId.toString() };
      }

      if (typeof labelId === 'string') {
        return { labelId };
      }

      return undefined;
    }
  }
};

const makeResultHeadline = ({ result }: Task): Record<string, string> | undefined => {
  if (result) {
    const { code, message, taskCollectionId, deviceId } = result;

    const headline: Record<string, string> = {};
    if (code) {
      headline.code = code as string;
    }

    if (message) {
      headline.message = message as string;
    }

    if (taskCollectionId) {
      headline.taskCollectionId = taskCollectionId as string;
    }

    if (deviceId) {
      headline.deviceId = deviceId as string;
    }

    if (Object.keys(headline).length > 0) {
      return headline;
    }
  }

  return undefined;
};
// ---

export const TaskManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const fetchPromises: CancelableFetchPromises = {};

  const pathParams = useParams();
  const navigate = useNavigate();

  useEffect(
    () => () => {
      cancelFetchPromises(fetchPromises);
    },
    [],
  );

  const preFilter = useMemo(() => {
    const [entityType, entityCoordinate, entityName] = (pathParams['*'] ?? '').split('/');

    const filter: Record<string, string> = {};
    if (entityType === 'event-rules') {
      if (entityName) {
        filter.eventRuleName = entityName;
      }
      if (entityCoordinate) {
        filter.eventRuleCoordinate = entityCoordinate;
      }
    }

    if (entityType === 'collection') {
      if (entityCoordinate) {
        filter.taskCollection = entityCoordinate;
      }
    }

    return filter;
  }, [pathParams]);

  const tableTitle = useMemo(() => {
    const { entityCoordinate, entityName, entityType } = pathParams;

    if (entityType && entityCoordinate && entityName) {
      return `${entityType} [${entityName}@${entityCoordinate}] Tasks`;
    }

    return undefined;
  }, [pathParams]);

  const onBack = useMemo(() => {
    const { entityType } = pathParams;

    if (entityType === 'event-rules') {
      return () => navigate('eventrules');
    }
    return undefined;
  }, [navigate, pathParams.entityType]);

  const fetchTasks = useCallback((pagination: Pagination) => api.getTasks(pagination), [api]);

  const table = useMemo(
    () => (
      <DataTable
        title={tableTitle}
        onBack={onBack}
        preFilter={preFilter}
        fetchItems={fetchTasks}
        defaultSort={{
          sortField: 'T.id',
          direction: 'desc',
        }}
        columns={[
          {
            header: 'ID',
            body: (entry: Task) => entry.id,
            filterField: 'id',
            sortField: 'T.id',
            searchTooltip: 'has to match exactly',
            fitContent: true,
          },
          {
            header: 'Task-Collection ID',
            body: (entry: Task) => entry.taskCollectionId,
            filterField: 'taskCollection',
            sortField: 'TC.id',
            searchTooltip: 'has to match exactly',
            fitContent: true,
          },
          {
            header: 'Status',
            body: (entry: Task) => {
              const { state } = entry;
              let color: string;
              switch (state) {
                case 'finished':
                  color = green[700];
                  break;
                case 'failed':
                  color = red[700];
                  break;
                case 'running':
                  color = yellow[700];
                  break;
                default:
                  color = 'textPrimary';
              }

              return (
                <Typography fontWeight={700} color={color}>
                  {state}
                </Typography>
              );
            },
            filterField: 'state',
            sortField: 'T.state',
            fitContent: true,
          },
          {
            header: 'Type',
            body: (entry: Task) => entry.instruction.method,
            filterField: 'type',
            fitContent: true,
          },
          {
            header: 'Parameters',
            filterField: 'instructionPath',
            filterInputOptions: {
              variant: 'json',
              basePath: 'parameters',
            },
            searchTooltip: <JsonPathParameterDescription />,
            body: (entry: Task) => (
              <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
                <RawJsonField
                  headline={makeParamHeadline(entry)}
                  expandedTitle={`Task [${entry.id}] Parameters`}
                  json={entry.instruction.parameters}
                />
              </ScrollableStack>
            ),
            proportionalContent: '50%',
          },
          {
            header: 'Result',
            filterField: 'resultPath',
            filterInputOptions: { variant: 'json' },
            searchTooltip: <JsonPathParameterDescription />,
            body: (entry: Task) => {
              const { result } = entry;

              if (result) {
                return (
                  <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
                    <RawJsonField
                      headline={makeResultHeadline(entry)}
                      headlineColorMap={{
                        code: (value) => {
                          const valueNumber = parseInt(value, 10);
                          switch (true) {
                            case valueNumber >= 200 && valueNumber < 300:
                              return 'green';
                            case valueNumber > 300:
                              return 'red';
                            default:
                              return undefined;
                          }
                        },
                      }}
                      expandedTitle={`Task [${entry.id}] Result`}
                      json={result}
                    />
                  </ScrollableStack>
                );
              }

              return '-';
            },
            proportionalContent: '50%',
          },
          {
            header: 'Updated-At',
            filterField: 'updatedAt',
            sortField: 'T.updatedAt',
            filterInputOptions: {
              variant: 'date',
            },
            body: (entry: Task) => {
              const { createdAt, updatedAt } = entry;
              return updatedAt ?? createdAt;
            },
            fitContent: true,
          },
        ]}
      />
    ),
    [fetchTasks, preFilter],
  );

  return <ContentControl>{table}</ContentControl>;
};
